// Hook: resolves a scan query (IMEI/barcode/SKU) against the /v1/scan endpoint.
// Returns the resolve function and its current result. Does NOT add items to any
// order/transfer — the caller (POS, transfer page, B2B) decides what to do with
// the result based on `kind` and `ok`.
import { useState, useCallback } from "react";
import { apiGet } from "@/lib/api/client";

// ── Response types ────────────────────────────────────────────────────────────

export interface ScanVariantInfo {
  id: string;
  sku: string;
  productName: string;
}

export interface ScanSerialInfo {
  id: string;
  serialNo: string;
  status: string;
  warehouseId?: string | null;
  warehouseName?: string | null;
}

export interface VariantSearchMatch {
  id: string;
  sku: string;
  productName: string;
  barcode?: string | null;
  sellPrice: string;
}

/** Exact serial found and its availability in the context warehouse. */
export interface ScanResultSerial {
  kind: "serial";
  variant: ScanVariantInfo;
  serial: ScanSerialInfo;
  /** True when status='in_stock' AND serial is in the context warehouse. */
  ok: boolean;
  /** Human-readable reason when ok=false, e.g. "đã bán" or "ở kho X, không phải kho Y". */
  reason?: string;
}

/** One option when a short alias matches several in-stock serials. */
export interface ScanSerialCandidate {
  variant: ScanVariantInfo;
  serial: ScanSerialInfo;
  ok: boolean;
  reason?: string;
}

/** Alias trúng nhiều máy còn tồn — nhân viên tự chọn từ danh sách. */
export interface ScanResultSerials {
  kind: "serials";
  serials: ScanSerialCandidate[];
}

/** No exact serial match — fuzzy variant results returned instead. */
export interface ScanResultVariant {
  kind: "variant";
  matches: VariantSearchMatch[];
}

/** Nothing matched. */
export interface ScanResultNone {
  kind: "none";
}

export type ScanResult = ScanResultSerial | ScanResultSerials | ScanResultVariant | ScanResultNone;

// ── Context options ───────────────────────────────────────────────────────────

export interface ScanContextSell {
  context: "sell";
  /** The active draft order ID. Server derives the warehouse from the order's workstation. */
  orderId: string;
}

export interface ScanContextTransfer {
  context: "transfer";
  /** The source warehouse ID chosen by the user. */
  warehouseId: string;
}

export type ScanContext = ScanContextSell | ScanContextTransfer;

// ── Hook ─────────────────────────────────────────────────────────────────────

export interface UseScanResolveReturn {
  /** Resolves q via /v1/scan. Returns the result (also stored in `result`). */
  resolve: (q: string) => Promise<ScanResult>;
  /** Latest resolve result. Null before first call. */
  result: ScanResult | null;
  /** True while a fetch is in flight. */
  loading: boolean;
  /** Last fetch error, if any. */
  error: Error | null;
  /** Clears the current result and error. */
  reset: () => void;
}

/**
 * useScanResolve — resolves a scan query against /v1/scan.
 *
 * Context wiring:
 *   - sell: pass { context: "sell", orderId } — server derives warehouse from order.
 *   - transfer: pass { context: "transfer", warehouseId } — source warehouse.
 *
 * IMPORTANT for callers that call updateItem after a successful serial scan:
 *   updateItem REPLACES the full serial set on the line. Always send the
 *   complete merged array: [...existingSerialIds, newSerialId]. Deduplicate
 *   before sending to avoid the serial-count invariant rejecting duplicates.
 */
export function useScanResolve(scanCtx: ScanContext): UseScanResolveReturn {
  const [result, setResult] = useState<ScanResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const resolve = useCallback(
    async (q: string): Promise<ScanResult> => {
      setLoading(true);
      setError(null);
      try {
        const params: Record<string, string> = {
          context: scanCtx.context,
          q,
        };
        if (scanCtx.context === "sell") {
          params.orderId = scanCtx.orderId;
        } else {
          params.warehouseId = scanCtx.warehouseId;
        }

        const res = await apiGet<ScanResult>("/v1/scan", params);
        setResult(res);
        return res;
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        const none: ScanResultNone = { kind: "none" };
        setResult(none);
        return none;
      } finally {
        setLoading(false);
      }
    },
    // Re-create only when context identity changes (stable for a given order/warehouse session).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scanCtx.context, (scanCtx as ScanContextSell).orderId, (scanCtx as ScanContextTransfer).warehouseId]
  );

  const reset = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { resolve, result, loading, error, reset };
}
